import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import CustomWarning from '../../shared/components/CustomWarning';
import { useDeleteGroup, useDuplicateUser, useGroupOptions, useGroups, useMoveUser, useRefreshGroupManager, useRemoveUserFromGroup } from '../../hooks/useGroupManager';
import { useInfiniteScrollSentinel } from '../../hooks/useInfiniteScrollSentinel';
import type { GroupManagerGroup, GroupMember } from '../../services/GroupManager/groupManager.service';
import GroupPanel from './components/GroupPanel';
import GroupFormModal from './components/GroupFormModal';
import UserProfileModal from '../UserManagement/components/UserProfileModal';
import UserFormModal from '../UserManagement/components/UserFormModal';
import { GroupMemberAction } from './components/GroupTables/columns';
import styles from './GroupManager.module.scss';

const { Title } = Typography;

const GroupManager = () => {
  const { groups, isLoading, isFetchingNextPage, isError, error, hasMore, loadMore } = useGroups();
  const { groupOptions } = useGroupOptions();
  const [openGroupIds, setOpenGroupIds] = useState<Set<number>>(new Set());
  // groupId is kept through the close animation so the modal doesn't flip to "create"
  // while fading out; `session` remounts the form on every open so it starts clean.
  const [groupForm, setGroupForm] = useState<{ open: boolean; groupId?: number; session: number }>({ open: false, session: 0 });
  // Same profile -> edit flow as User Manager, reusing its modals.
  const [profileUserId, setProfileUserId] = useState<number | undefined>(undefined);
  const [editUserId, setEditUserId] = useState<number | undefined>(undefined);
  const refreshGroupManager = useRefreshGroupManager();
  const [pendingDelete, setPendingDelete] = useState<{ groupId: number; anchor: HTMLElement | null } | null>(null);
  const groupRefs = useRef(new Map<number, HTMLElement>());
  const groupRefCallbacks = useRef(new Map<number, (node: HTMLElement | null) => void>());
  const revealFrame = useRef<number | null>(null);

  useEffect(() => () => {
    if (revealFrame.current !== null) cancelAnimationFrame(revealFrame.current);
  }, []);

  // Opens the group(s) regardless of whether they're paginated in yet -- a
  // group on a page that hasn't loaded still ends up open once it arrives.
  const openGroups = useCallback((groupIds: number[]) => {
    setOpenGroupIds((current) => new Set([...current, ...groupIds]));
  }, []);

  // Same, plus scrolls the first group into view if its panel is already
  // mounted (i.e. its page has loaded); a not-yet-loaded group just opens.
  const revealGroups = useCallback((groupIds: number[]) => {
    openGroups(groupIds);
    const loadedGroupId = groupIds.find((groupId) => groupRefs.current.has(groupId));
    if (loadedGroupId === undefined) return;
    if (revealFrame.current !== null) cancelAnimationFrame(revealFrame.current);
    revealFrame.current = requestAnimationFrame(() => groupRefs.current.get(loadedGroupId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }, [openGroups]);

  const handleDeleteAffected = useCallback((groupId: number) => {
    setOpenGroupIds((current) => {
      const next = new Set(current);
      next.delete(groupId);
      return next;
    });
  }, []);
  const deleteGroup = useDeleteGroup(handleDeleteAffected);
  const removeUser = useRemoveUserFromGroup((groupId) => revealGroups([groupId]));
  const duplicateUser = useDuplicateUser(revealGroups);
  const moveUser = useMoveUser(openGroups);

  const handleToggleGroup = useCallback((groupId: number) => {
    setOpenGroupIds((ids) => {
      const next = new Set(ids);
      if (!next.delete(groupId)) next.add(groupId);
      return next;
    });
  }, []);

  const openCreateGroup = useCallback(() => setGroupForm((current) => ({ open: true, session: current.session + 1 })), []);
  const handleEditGroup = useCallback((groupId: number) => setGroupForm((current) => ({ open: true, groupId, session: current.session + 1 })), []);
  const closeGroupForm = useCallback(() => setGroupForm((current) => ({ ...current, open: false })), []);

  // Stable per-group id so passing it to memo(GroupPanel) doesn't force a
  // detach/reattach of the ref on every render.
  const getGroupRefCallback = useCallback((groupId: number) => {
    let callback = groupRefCallbacks.current.get(groupId);
    if (!callback) {
      callback = (node) => {
        if (node) groupRefs.current.set(groupId, node);
        else groupRefs.current.delete(groupId);
      };
      groupRefCallbacks.current.set(groupId, callback);
    }
    return callback;
  }, []);

  const closeUserProfile = useCallback(() => setProfileUserId(undefined), []);
  const handleEditFromProfile = useCallback((userId: number) => {
    setProfileUserId(undefined);
    setEditUserId(userId);
  }, []);
  const closeUserForm = useCallback(() => setEditUserId(undefined), []);

  const handleGroupDelete = useCallback((groupId: number, anchor: HTMLElement | null) => setPendingDelete({ groupId, anchor }), []);

  const { execute: removeUserExecute } = removeUser;
  const { execute: duplicateUserExecute } = duplicateUser;
  const { execute: moveUserExecute } = moveUser;
  const handleMemberAction = useCallback((action: GroupMemberAction, group: GroupManagerGroup, member: GroupMember, _anchor: HTMLElement | null, targetGroupId?: number) => {
    if (action === GroupMemberAction.VIEW_PROFILE) {
      setProfileUserId(member.id);
    }
    if (action === GroupMemberAction.REMOVE) {
      void removeUserExecute({ groupId: group.id, userId: member.id }).catch(() => undefined);
    }
    if (action === GroupMemberAction.DUPLICATE && targetGroupId !== undefined) {
      void duplicateUserExecute({ userId: member.id, targetGroupId, sourceGroupId: group.id }).catch(() => undefined);
    }
    if (action === GroupMemberAction.MOVE && targetGroupId !== undefined) {
      void moveUserExecute({ groupId: group.id, userId: member.id, targetGroupIds: [targetGroupId] }).catch(() => undefined);
    }
  }, [duplicateUserExecute, moveUserExecute, removeUserExecute]);

  const listSentinelRef = useInfiniteScrollSentinel({
    onReachEnd: loadMore,
    enabled: hasMore && !isFetchingNextPage,
  });

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <Title level={2} className={styles.title}>
          Group Manager
        </Title>
        <button type="button" className={styles.addButton} onClick={openCreateGroup}>
          <img src="/assets/upload-file-icon.svg" alt="" className={styles.addIcon} />
          Add New Group
        </button>
      </header>
      <main className={styles.groupList}>
        <div className={styles.groupListInner}>
          {isLoading ? <p className={styles.message}>Loading groups...</p> : null}
          {isError ? <p className={styles.message}>{error?.message ?? 'Unable to load groups.'}</p> : null}
          {!isLoading && !isError && groups.length === 0 ? <p className={styles.message}>No groups available</p> : null}
          {!isLoading && !isError ? groups.map((group) => (
            <GroupPanel
              key={group.id}
              ref={getGroupRefCallback(group.id)}
              group={group}
              groups={groupOptions}
              isOpen={openGroupIds.has(group.id)}
              onToggle={handleToggleGroup}
              onEdit={handleEditGroup}
              onDelete={handleGroupDelete}
              onMemberAction={handleMemberAction}
            />
          )) : null}
          {isFetchingNextPage ? <p className={styles.message}>Loading more groups...</p> : null}
          <div ref={listSentinelRef} className={styles.sentinel} aria-hidden="true" />
        </div>
      </main>
      <UserProfileModal
        open={profileUserId !== undefined}
        userId={profileUserId}
        onClose={closeUserProfile}
        onEdit={handleEditFromProfile}
      />
      <UserFormModal open={editUserId !== undefined} onClose={closeUserForm} isEditing userId={editUserId} onCreated={refreshGroupManager} />
      <GroupFormModal key={groupForm.session} open={groupForm.open} groupId={groupForm.groupId} onClose={closeGroupForm} />
      <CustomWarning
        open={!!pendingDelete}
        anchorEl={pendingDelete?.anchor}
        showIcon
        size="large"
        variant="dark"
        message={<>THIS WILL PERMANENTLY remove this group from the system.<br />the users within this group will not be deleted.</>}
        cancelText="NO, GO BACK"
        confirmText="YES, DELETE"
        isLoading={deleteGroup.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          void deleteGroup.execute(pendingDelete.groupId).then(() => setPendingDelete(null)).catch(() => undefined);
        }}
      />
    </div>
  );
};

export default GroupManager;
